import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import TopBar from './TopBar';
import { getOrderById, getOrderItems, updateOrderItemStatus } from '../services/orderService';
import { getPickupPointById } from '../services/pickupPointService';
import { getFoodItemById, getFoodImageUrl } from '../services/foodItemService';
import { getProfileByUserId } from '../services/profileService';
import { getReviewByOrderItemId } from '../services/reviewService';
import {
  canConfirmReceipt,
  canConfirmReceiptNow,
  shouldAutoComplete,
  getTimeUntilCanConfirm,
  getTimeUntilAutoComplete,
  shouldShowConfirmReceipt
} from '../utils/orderTimeUtils';
import './BuyerOrderDetailPage.css';

const BuyerOrderDetailPage = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const location = useLocation();
  const [order, setOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState("all");
  const [updatingItemId, setUpdatingItemId] = useState(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch the order
        const orderData = await getOrderById(orderId);

        // Verify this order belongs to the current user
        if (orderData.customer_id !== user.$id) {
          setError('You do not have permission to view this order.');
          setLoading(false);
          return;
        }

        // We'll determine pickup locations per item (use food item's pickup_id instead of order.pickup_id)
        const pickupCache = {};

        // Get order items with food details
        const items = await getOrderItems(orderId);
        const enrichedItems = await Promise.all(
          items.map(async (item) => {
            let foodData = null;
            let vendorName = 'Unknown Vendor';
            try {
              foodData = await getFoodItemById(item.food_id);
              // Get vendor profile
              try {
                const vendorProfile = await getProfileByUserId(foodData.vendor_id);
                vendorName = vendorProfile?.vendor_name || vendorProfile?.full_name || 'Unknown Vendor';
              } catch (vendorError) {
                console.error('Error fetching vendor profile:', vendorError);
              }
            } catch (foodError) {
              console.error('Error fetching food item:', foodError);
            }

            // Resolve pickup location from the food item's pickup_id (not order.pickup_id)
            let pickupId = foodData?.pickup_id || null;
            let pickupLocationName = 'Not specified';
            if (pickupId) {
              if (!pickupCache[pickupId]) {
                try {
                  const pickupPoint = await getPickupPointById(pickupId);
                  pickupCache[pickupId] = pickupPoint?.fullName || pickupPoint?.name || 'Not specified';
                } catch (pickupErr) {
                  console.error('Error fetching pickup point for item:', pickupErr);
                  pickupCache[pickupId] = 'Not specified';
                }
              }
              pickupLocationName = pickupCache[pickupId];
            }

            // Check if buyer already left a review for this order item
            let hasReview = false;
            try {
              const existing = await getReviewByOrderItemId(item.$id);
              hasReview = !!existing;
            } catch (revErr) {
              console.error('Error checking review for order item:', revErr);
            }

            return {
              id: item.$id,
              foodId: item.food_id,
              name: foodData?.item_name || 'Unknown Item',
              quantity: item.quantity,
              priceAtPurchase: item.price_at_purchase,
              status: item.status || 'pending',
              imageUrl: foodData?.image_id ? getFoodImageUrl(foodData.image_id) : null,
              vendorName: vendorName,
              vendorId: foodData?.vendor_id || null,
              pickupTime: foodData?.pickup_time || 'Not specified',
              pickupId: pickupId,
              pickupLocation: pickupLocationName,
              hasReview: hasReview
            };
          })
        );

        // Summarize pickup locations for the order (derive from each item's food pickup_id)
        const uniquePickupLocations = Array.from(new Set(enrichedItems.map(i => i.pickupLocation || 'Not specified')));
        const pickupLocationSummary = uniquePickupLocations.length === 1 ? uniquePickupLocations[0] : 'Multiple pickup locations';

        setOrder({
          id: orderData.$id,
          customerId: orderData.customer_id,
          totalAmount: orderData.total_amount,
          pointsRedeemed: orderData.points_redeemed,
          orderDate: orderData.order_date,
          pickupLocation: pickupLocationSummary
        });

        setOrderItems(enrichedItems);
      } catch (err) {
        console.error('Error fetching order details:', err);
        setError('Failed to load order details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [user, orderId, navigate]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }) + ', ' + date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatOrderId = (id) => {
    if (!id) return '';
    // Display the raw Appwrite document ID ($id) from the order document
    return id;
  };

  const formatPickupTime = (timeString) => {
    if (!timeString || timeString === 'Not specified') return 'Not specified';
    try {
      const date = new Date(timeString);
      if (isNaN(date.getTime())) return timeString;
      return date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }) + ', ' + date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return timeString;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'preparing': return 'Preparing';
      case 'ready_for_pickup': return 'Ready for Pickup';
      case 'completed': return 'Completed';
      case 'not_completed': return 'Not Completed';
      default: return 'Unknown';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'preparing': return 'status-preparing';
      case 'ready_for_pickup': return 'status-ready';
      case 'completed': return 'status-completed';
      case 'not_completed': return 'status-not-completed';
      default: return '';
    }
  };

  const getProgressStep = (status) => {
    switch (status) {
      case 'pending': return 0;
      case 'preparing': return 1;
      case 'ready_for_pickup': return 2;
      case 'completed': return 3;
      case 'not_completed': return -1;
      default: return 0;
    }
  };

  // Helper to get the index of the furthest completed step for rendering purposes
  const renderProgressIndex = (status) => {
    // If buyer marked not completed, assume they reached Ready For Pickup before reporting not received
    if (status === 'not_completed') return 2;
    return getProgressStep(status);
  };

  // State for live countdown timer
  const [countdownTick, setCountdownTick] = useState(0);

  // Live countdown timer - updates every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCountdownTick(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Auto-complete items that have passed the auto-complete period
  // Works regardless of status (pending, ready_for_pickup, etc.)
  useEffect(() => {
    const autoCompleteItems = async () => {
      for (const item of orderItems) {
        // Only auto-complete if not already completed or marked as not completed
        if (item.status !== 'completed' && item.status !== 'not_completed' && shouldAutoComplete(item.pickupTime)) {
          try {
            console.log(`Auto-completing order item ${item.id} - buyer did not respond within auto-complete period`);
            await updateOrderItemStatus(item.id, 'completed');
            setOrderItems(prevItems =>
              prevItems.map(i =>
                i.id === item.id ? { ...i, status: 'completed' } : i
              )
            );
          } catch (err) {
            console.error('Error auto-completing order item:', err);
          }
        }
      }
    };

    if (orderItems.length > 0) {
      autoCompleteItems();
    }
    // Re-check periodically (every minute)
    const interval = setInterval(autoCompleteItems, 60000);
    return () => clearInterval(interval);
  }, [orderItems.length]); // Only run when orderItems are first loaded

  // Handle buyer confirming receipt
  const handleConfirmReceipt = async (itemId, received) => {
    try {
      setUpdatingItemId(itemId);
      const newStatus = received ? 'completed' : 'not_completed';
      await updateOrderItemStatus(itemId, newStatus);

      // Update local state
      setOrderItems(prevItems =>
        prevItems.map(item =>
          item.id === itemId ? { ...item, status: newStatus } : item
        )
      );

      showSuccess(received ? 'Thank you for confirming receipt!' : 'We\'ve noted that you didn\'t receive this item.');
    } catch (err) {
      console.error('Error updating receipt status:', err);
      showError('Failed to update status. Please try again.');
    } finally {
      setUpdatingItemId(null);
    }
  };

  // Calculate overall order status based on item statuses
  const getOverallStatus = () => {
    if (orderItems.length === 0) return 'pending';

    const statuses = orderItems.map(item => item.status);

    // If all completed
    if (statuses.every(s => s === 'completed')) return 'completed';
    // If all not_completed (every item reported not received)
    if (statuses.every(s => s === 'not_completed')) return 'not_completed';
    // If any not_completed (mixed cases)
    if (statuses.some(s => s === 'not_completed')) return 'partial';
    // If all ready
    if (statuses.every(s => s === 'ready_for_pickup' || s === 'completed')) return 'ready_for_pickup';
    // If any preparing
    if (statuses.some(s => s === 'preparing')) return 'preparing';
    // Default to pending
    return 'pending';
  };

  if (loading) {
    return (
      <div className="buyer-order-detail-page">
        <TopBar />
        <div className="buyer-order-detail-container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading order details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="buyer-order-detail-page">
        <TopBar />
        <div className="buyer-order-detail-container">
          <div className="error-state">
            <p>{error}</p>
            <button onClick={() => navigate('/orders', { state: { viewMode: location.state?.viewMode || 'buyer', tab: location.state?.tab || 'all' } })} className="back-btn">
              Back to Orders
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="buyer-order-detail-page">
      <TopBar />
      <div className="buyer-order-detail-container">
        <button className="back-link" onClick={() => navigate('/orders', { state: { viewMode: location.state?.viewMode || 'buyer', tab: location.state?.tab || 'all' } })}>
          ← Back to Orders
        </button>

        <h1 className="page-title">Order Details</h1>
        <p className="page-subtitle">View your order status and items</p>

        {order && (
          <>
            {/* Order Summary Card */}
            <div className="order-summary-card">
              <h2 className="section-title">Order Summary</h2>
              <div className="order-meta">
                <div className="meta-row">
                  <span className="meta-label">Order ID:</span>
                  <span className="meta-value order-id">{formatOrderId(order.id)}</span>
                </div>
                <div className="meta-row">
                  <span className="meta-label">Order Date:</span>
                  <span className="meta-value">{formatDate(order.orderDate)}</span>
                </div>
                <div className="meta-row">
                  <span className="meta-label">Pickup Location:</span>
                  <span className="meta-value">{order.pickupLocation}</span>
                </div>
                <div className="meta-row">
                  <span className="meta-label">Total Amount:</span>
                  <span className="meta-value total-amount">RM {order.totalAmount?.toFixed(2)}</span>
                </div>
                {order.pointsRedeemed > 0 && (
                  <div className="meta-row">
                    <span className="meta-label">Points Redeemed:</span>
                    <span className="meta-value points">{order.pointsRedeemed} pts</span>
                  </div>
                )}
              </div>
            </div>

            {/* Order Items List */}
            <div className="order-items-section">
              <h2 className="section-title">Order Items ({orderItems.length})</h2>
              <div className="items-list">
                {orderItems.map((item) => (
                  <div
                    key={item.id}
                    className={`item-card ${selectedItem === item.id ? 'selected' : ''}`}
                    onClick={() => setSelectedItem(selectedItem === item.id ? null : item.id)}
                  >
                    <div className="item-main">
                      <div className="item-image">
                        {item.imageUrl ? (
                          <img src={item.imageUrl} alt={item.name} />
                        ) : (
                          <div className="image-placeholder">🍽️</div>
                        )}
                      </div>
                      <div className="item-info">
                        <h3 className="item-name">{item.name}</h3>
                        <p className="item-vendor">by {item.vendorName}</p>
                        <p className="item-quantity">Quantity: {item.quantity}</p>
                      </div>
                      <div className="item-price">
                        <span className="price">RM {(item.priceAtPurchase * item.quantity).toFixed(2)}</span>
                      </div>
                    </div>

                    <div className={`item-status-badge ${getStatusClass(item.status)}`}>
                      {getStatusText(item.status)}
                    </div>

                    {(selectedItem === 'all' || selectedItem === item.id) && (
                      <div className="item-details-expanded" onClick={(e) => e.stopPropagation()}>
                        <div className="progress-section">
                          <h4>Order Progress</h4>
                          <div className="progress-tracker">
                            <div className={`progress-step ${renderProgressIndex(item.status) >= 0 ? 'completed' : ''} ${renderProgressIndex(item.status) === 0 ? 'current' : ''}`}>
                              <div className="step-icon">✓</div>
                              <span>Pending</span>
                            </div>
                            <div className={`progress-line ${renderProgressIndex(item.status) >= 1 ? 'active' : ''}`}></div>
                            <div className={`progress-step ${renderProgressIndex(item.status) >= 1 ? 'completed' : ''} ${renderProgressIndex(item.status) === 1 ? 'current' : ''}`}>
                              <div className="step-icon">🍳</div>
                              <span>Preparing</span>
                            </div>
                            <div className={`progress-line ${renderProgressIndex(item.status) >= 2 ? 'active' : ''}`}></div>
                            <div className={`progress-step ${renderProgressIndex(item.status) >= 2 ? 'completed' : ''} ${renderProgressIndex(item.status) === 2 ? 'current' : ''}`}>
                              <div className="step-icon">📦</div>
                              <span>Ready For Pickup</span>
                            </div>
                            <div className={`progress-line ${(item.status === 'not_completed' || renderProgressIndex(item.status) >= 3) ? 'active' : ''}`}></div>

                            {item.status === 'not_completed' ? (
                              <div className={`progress-step not-completed current`}>
                                <div className="step-icon">✗</div>
                                <span>Not Completed</span>
                              </div>
                            ) : (
                              <div className={`progress-step ${renderProgressIndex(item.status) >= 3 ? 'completed' : ''} ${renderProgressIndex(item.status) === 3 ? 'current' : ''}`}>
                                <div className="step-icon">{renderProgressIndex(item.status) >= 3 ? '✓' : '✅'}</div>
                                <span>Completed</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="item-extra-info">
                          <p><strong>Scheduled Pickup Time:</strong> {formatPickupTime(item.pickupTime)}</p>
                          <p><strong>Pickup Location:</strong> {item.pickupLocation}</p>
                          <p><strong>Unit Price:</strong> RM {item.priceAtPurchase.toFixed(2)}</p>
                        </div>

                        {/* Confirm Receipt Section - show based on pickup time, not just status */}
                        {shouldShowConfirmReceipt(item.status, item.pickupTime) && (
                          <div className="confirm-receipt-section">
                            <h4>Confirm Receipt</h4>
                            {canConfirmReceiptNow(item.status, item.pickupTime) ? (
                              <>
                                <p className="confirm-instruction">Did you receive this item?</p>
                                <div className="confirm-buttons">
                                  <button
                                    className="confirm-btn received"
                                    onClick={() => handleConfirmReceipt(item.id, true)}
                                    disabled={updatingItemId === item.id}
                                  >
                                    {updatingItemId === item.id ? 'Updating...' : '✓ Yes, Received'}
                                  </button>
                                  <button
                                    className="confirm-btn not-received"
                                    onClick={() => handleConfirmReceipt(item.id, false)}
                                    disabled={updatingItemId === item.id}
                                  >
                                    {updatingItemId === item.id ? 'Updating...' : '✗ Not Received'}
                                  </button>
                                </div>
                                <p className="auto-complete-notice">
                                  ⏱️ Auto-complete in: <strong>{getTimeUntilAutoComplete(item.pickupTime) || 'Soon'}</strong>
                                </p>
                              </>
                            ) : (
                              <div className="grace-period-notice">
                                <p className="waiting-message">
                                  ⏳ You can confirm receipt in: <strong>{getTimeUntilCanConfirm(item.pickupTime)}</strong>
                                </p>
                                <p className="grace-period-info">
                                  (Grace period: 10 minutes after scheduled pickup time)
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Show final status message for completed/not completed items */}
                        {item.status === 'completed' && (
                          <div className="status-message success">
                            ✓ You confirmed receiving this item
                          </div>
                        )}
                        {item.status === 'not_completed' && (
                          <div className="status-message error">
                            ✗ You reported not receiving this item
                          </div>
                        )}

                        {/* Leave review CTA for completed / not_completed items */}
                        {(item.status === 'completed' || item.status === 'not_completed') && !item.hasReview && (
                          <div className="leave-review-section">
                            <button
                              className="leave-review-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/create-review/${item.id}`, { state: { vendorId: item.vendorId, vendorName: item.vendorName } });
                              }}
                            >
                              Leave a Review for {item.vendorName}
                            </button>
                          </div>
                        )}

                        {item.hasReview && (
                          <div className="status-message info">
                            You have already left a review for this item. Thank you!
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BuyerOrderDetailPage;
